import { Button, Dropdown, Radio, RadioGroup, Spacing } from '@billy/design-system'
import { ChangeEvent, ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { InvoiceDiscountMode } from '@features/invoices/enums/invoiceDiscountMode'
import { isInvoiceDiscountMode } from '@features/invoices/utils/isInvoiceDiscountMode'
import { isInvoiceFormRowLine } from '@features/invoices/utils/isInvoiceFormRowLine'

import { useFormContext } from '../../../../hooks'
import { DemoInvoiceFormSchema } from '../../types/demoInvoiceFormSchema'
import * as Styled from './styles'

interface DemoInvoiceDiscountsProps {
  discountMode: InvoiceDiscountMode
  onDiscountModeChange: (discountMode: InvoiceDiscountMode) => void
}

export const DemoInvoiceDiscounts = ({
  discountMode,
  onDiscountModeChange,
}: DemoInvoiceDiscountsProps): ReactElement => {
  const { t } = useTranslation()
  const { getValues, setValue } = useFormContext<DemoInvoiceFormSchema>()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const discountModeOptions = useMemo(
    () => [
      {
        label: t('external_invoices.editor.form.field.invoice_lines.discounts.mode.none'),
        value: InvoiceDiscountMode.None,
      },
      {
        label: t('external_invoices.editor.form.field.invoice_lines.discounts.mode.by_item'),
        value: InvoiceDiscountMode.ByItem,
      },
      {
        label: t('external_invoices.editor.form.field.invoice_lines.discounts.mode.global'),
        value: InvoiceDiscountMode.Global,
      },
    ],
    [t],
  )

  const handleDropdownOpen = useCallback(() => setIsDropdownOpen(true), [])
  const handleDropdownClose = useCallback(() => setIsDropdownOpen(false), [])

  const handleDiscountModeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.currentTarget

      if (!isInvoiceDiscountMode(value)) {
        return
      }

      if (value !== InvoiceDiscountMode.ByItem) {
        for (const [lineIndex, line] of getValues('invoiceLines').entries()) {
          if (isInvoiceFormRowLine(line)) {
            setValue(`invoiceLines.${lineIndex}.discount`, 0)
          }
        }
      }

      if (value !== InvoiceDiscountMode.Global) {
        setValue('discount', 0)
      }

      onDiscountModeChange(value)
    },
    [getValues, onDiscountModeChange, setValue],
  )

  return (
    <Dropdown
      isOpen={isDropdownOpen}
      onClose={handleDropdownClose}
      onOpen={handleDropdownOpen}
      placement="bottom-start"
      size="fitTrigger"
      trigger={
        <Button
          active={isDropdownOpen}
          icon="percent"
          iconRight={isDropdownOpen ? 'chevronUp' : 'chevronDown'}
          size="md"
          variant="text"
        >
          {t('external_invoices.editor.form.field.invoice_lines.discounts')}
        </Button>
      }
    >
      <Styled.DiscountsOptionsWrapper>
        <RadioGroup direction="column" onChange={handleDiscountModeChange} spacing={Spacing.S16} value={discountMode}>
          {discountModeOptions.map(({ label, value }) => (
            <Radio key={value} value={value}>
              {label}
            </Radio>
          ))}
        </RadioGroup>
      </Styled.DiscountsOptionsWrapper>
    </Dropdown>
  )
}
